import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import { Wallet } from 'lucide-react';
import EligibleCashbackIcon from '../images/EligibleCashback.svg';
import RedeemableCashbackIcon from '../images/RedeemableCashback.svg';
import LifetimeCashbackIcon from '../images/LifetimeCashback.svg';
import { 
  getSubscriberProfile, 
  getWalletSnapshot,
  getMedicineBookingSnapshot,
  getMedicineBookingDetail,
  updateMedicineBookingStatus,
  refundMedicineOrder
} from '../services/subscriberWalletService';
import { MEDICINE_LIST_PATH, MEDICINE_LIST_URL_KEY, resolveBookingBackTarget, resolveListBackTarget } from '../utils/subscriberHelpers';
import MedicineTransaction from './MedicineTransaction';
import MedicineRefundModal from '../components/medicine/MedicineRefundModal';
import MedicineBookingDetailModal from '../components/medicine/MedicineBookingDetailModal';

// Currency formatter with truncation to 2 decimals (no rounding)
const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') {
    return `₹${formattedWhole}`;
  }
  return `₹${formattedWhole}.${decimalPart}`;
};

// Status badge styling helper with new color scheme
const getStatusBadgeStyle = (status) => {
  const statusLower = String(status || '').toLowerCase().trim();
  
  // Map status values to color scheme (exact matches only)
  const statusColorMap = {
    'success': { text: '#16A34A', bg: 'rgba(22, 163, 74, 0.1)' },
    'failed': { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)' },
    'cancelled': { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)' },
    'in_progress': { text: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
    'in progress': { text: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
    'refunded': { text: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.1)' },
    'saved': { text: '#6B7280', bg: 'rgba(107, 114, 128, 0.1)' },
    'initiated': { text: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
    'out_of_stock': { text: '#B91C1C', bg: 'rgba(185, 28, 28, 0.1)' },
    'out of stock': { text: '#B91C1C', bg: 'rgba(185, 28, 28, 0.1)' },
    'ordered': { text: '#6366F1', bg: 'rgba(99, 102, 241, 0.1)' },
    'processed': { text: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.1)' },
    'dispatched': { text: '#14B8A6', bg: 'rgba(20, 184, 166, 0.1)' },
    'out_for_delivery': { text: '#F97316', bg: 'rgba(249, 115, 22, 0.1)' },
    'out for delivery': { text: '#F97316', bg: 'rgba(249, 115, 22, 0.1)' },
    'delivered': { text: '#22C55E', bg: 'rgba(34, 197, 94, 0.1)' },
    'complete': { text: '#15803D', bg: 'rgba(21, 128, 61, 0.1)' },
    'dropped': { text: '#7C2D12', bg: 'rgba(124, 45, 18, 0.1)' }
  };
  
  // Get exact match or default to saved (gray)
  const colorConfig = statusColorMap[statusLower] || statusColorMap['saved'];
  
  return {
    color: colorConfig.text,
    backgroundColor: colorConfig.bg,
    borderColor: colorConfig.text,
    minWidth: '100px',
    justifyContent: 'center'
  };
};

// Format date to DD-MMM-YYYY (e.g., 22-Nov-2025)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Handle case where dateString is an object with start_date and end_date
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    if (dateString.start_date && dateString.end_date) {
      // Format as date range - handle nested objects recursively
      let start = dateString.start_date;
      let end = dateString.end_date;
      
      // If start_date or end_date are objects, format them recursively
      if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
        start = formatDate(start);
      } else {
        start = formatDate(String(start));
      }
      
      if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
        end = formatDate(end);
      } else {
        end = formatDate(String(end));
      }
      
      return `${start} - ${end}`;
    } else {
      return 'N/A';
    }
  }
  
  // Ensure we're working with a string
  if (typeof dateString !== 'string') {
    return 'N/A';
  }
  
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let date;
    
    // If it's a date range (e.g., "2025-05-16 to 2026-05-16" or "16/05/2025 - 16/05/2026")
    if (dateString.includes(' to ') || dateString.includes(' - ')) {
      const separator = dateString.includes(' to ') ? ' to ' : ' - ';
      const [startDate, endDate] = dateString.split(separator);
      const formattedStart = formatDate(startDate.trim());
      const formattedEnd = formatDate(endDate.trim());
      return `${formattedStart} - ${formattedEnd}`;
    }
    
    // Handle DD/MM/YYYY or DD/MM/YY format
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      const parts = dateString.split('/');
      const day = parts[0].padStart(2, '0');
      const monthNum = parts[1].padStart(2, '0');
      let year = parts[2];
      
      // If year is 2 digits, convert to 4 digits
      if (year.length === 2) {
        const yearNum = parseInt(year, 10);
        year = yearNum > 50 ? `19${year}` : `20${year}`;
      }
      
      // Create date object to get month name
      date = new Date(`${year}-${monthNum}-${day}`);
      if (!isNaN(date.getTime())) {
        const month = monthNames[date.getMonth()];
        return `${day}-${month}-${year}`;
      }
    }
    
    // Try to parse the date
    date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch {
    return dateString;
  }
};

// Format date and time as DD-MMM-YYYY HH:MM AM/PM (12-hour format)
// Handles API format: "DD-MM-YYYY HH:MM" and converts to "DD-MMM-YYYY HH:MM AM/PM"
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Handle case where dateString is an object with start_date and end_date
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    // Prevent infinite recursion - if start_date or end_date are also objects, format them recursively
    if (dateString.start_date && dateString.end_date) {
      // Format as date range - handle nested objects recursively
      let start = dateString.start_date;
      let end = dateString.end_date;
      
      // If start_date or end_date are objects, format them recursively
      if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
        start = formatDateTime(start);
      } else {
        start = formatDateTime(String(start));
      }
      
      if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
        end = formatDateTime(end);
      } else {
        end = formatDateTime(String(end));
      }
      
      return `${start} to ${end}`;
    } else {
      // If it's an object but doesn't have the expected structure, return 'N/A' to avoid rendering the object
      return 'N/A';
    }
  }
  
  // Ensure we're working with a string
  if (typeof dateString !== 'string') {
    return 'N/A';
  }
  
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Check if the date is already in DD-MM-YYYY HH:MM format (from API)
    if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/.test(dateString)) {
      // Parse DD-MM-YYYY HH:MM format
      const [datePart, timePart] = dateString.split(' ');
      const [day, monthNum, year] = datePart.split('-');
      const [hours24, minutes] = timePart.split(':');
      
      // Convert month number to month abbreviation
      const month = monthNames[parseInt(monthNum, 10) - 1] || monthNum;
      
      // Convert to 12-hour format
      let hours = parseInt(hours24, 10);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      const formattedHours = String(hours).padStart(2, '0');
      
      return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
    }
    
    // Fallback: try to parse as standard date string
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    // Convert to 12-hour format
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, '0');
    
    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return 'N/A';
  }
};

const MedicineBookingProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');

  // Where the secondary back button goes: the subscriber this booking was opened from when
  // the user came through Transaction History, otherwise the Medicine list page/filters it was opened from.
  const backTarget = resolveBookingBackTarget(searchParams, {
    label: 'Back to Medicine',
    path: resolveListBackTarget(location.state, MEDICINE_LIST_PATH, MEDICINE_LIST_URL_KEY),
  });
  
  // State for API data
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [medicineSnapshot, setMedicineSnapshot] = useState(null);
  const [medicineSnapshotLoading, setMedicineSnapshotLoading] = useState(false);
  const [medicineSnapshotError, setMedicineSnapshotError] = useState(null);
  
  // State for detail modal
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [medicineTransactionKey, setMedicineTransactionKey] = useState(0);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [partnerDataExpanded, setPartnerDataExpanded] = useState(false);
  
  // State for medicine transaction modal
  const [showMedicineTransactionModal, setShowMedicineTransactionModal] = useState(false);
  const [walletTransactionIdForModal, setWalletTransactionIdForModal] = useState(null);
  
  // State for status update modal
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [selectedTransactionForStatus, setSelectedTransactionForStatus] = useState(null);
  const [thirdPartyOrderId, setThirdPartyOrderId] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // State for refund modal (Finance and SuperAdmin)
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundBookingId, setRefundBookingId] = useState(null);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState(null);
  const [successToast, setSuccessToast] = useState(null);
  
  // Medicine snapshot pagination
  const [snapshotPage, setSnapshotPage] = useState(1);
  const SNAPSHOT_PAGE_SIZE = 25;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoadingDataRef = useRef(false);
  const isSnapshotLoadingRef = useRef(false);
  const autoOpenedDetailRef = useRef(false);
  
  const [showPhone, setShowPhone] = useState(false);
  const [mobilesUnmasked] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  // Mount state for portal
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Mask mobile number function
  const maskMobile = (value) => {
    if (!value) return 'N/A';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length <= 4) return `******${digits}`;
    const last4 = digits.slice(-4);
    return `******${last4}`;
  };
  
  // Auto-detect role from authentication
  const [currentRole] = useState(() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance', 
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    if (roleMapping[authRole]) {
      return roleMapping[authRole];
    }
    return null;
  });
  
  // Role-based permissions
  const isFinance = currentRole === 'Finance';
  const isSupport = currentRole === 'Support';
  const isOpsOrSuperAdmin = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const isFinanceOrSuperAdmin = currentRole === 'Finance' || currentRole === 'SuperAdmin';
  const canRefund = currentRole === 'Ops' || currentRole === 'Finance' || currentRole === 'SuperAdmin';
  
  // Mask phone for display
  const maskedPhone = React.useMemo(() => {
    if (!profile) return 'N/A';
    const phone = profile?.personal?.mobile || profile?.phoneFull || '';
    if (!phone || phone.trim() === '') return 'N/A';
    return maskMobile(phone);
  }, [profile]);
  
  // Load profile and wallet data
  useEffect(() => {
    if (isLoadingDataRef.current || !id || !currentRole) {
      return;
    }
    
    isLoadingDataRef.current = true;
    
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [profileData, walletData] = await Promise.allSettled([
          getSubscriberProfile(id, currentRole).catch(err => {
            return { error: 'Profile API failed', details: err.message };
          }),
          getWalletSnapshot(id, currentRole).catch(err => {
            return { error: 'Wallet API failed', details: err.message };
          })
        ]);
        
        // Handle profile data
        if (profileData.status === 'fulfilled' && profileData.value) {
          const response = profileData.value;
          let profileToSet = null;
          
          if (response.msg && typeof response.msg === 'object') {
            profileToSet = response.msg;
          } else if (response.data && typeof response.data === 'object') {
            profileToSet = response.data;
          } else if (typeof response === 'object') {
            profileToSet = response;
          }
          
          if (profileToSet) {
            setProfile(profileToSet);
          }
        }
        
        // Handle wallet data
        if (walletData.status === 'fulfilled' && walletData.value) {
          const response = walletData.value;
          let walletToSet = null;
          
          if (response.wallet_snapshot) {
            walletToSet = { wallet_snapshot: response.wallet_snapshot };
          } else if (response.msg && response.msg.wallet_snapshot) {
            walletToSet = { wallet_snapshot: response.msg.wallet_snapshot };
          } else if (response.data && response.data.wallet_snapshot) {
            walletToSet = { wallet_snapshot: response.data.wallet_snapshot };
          } else if (response.error) {
            walletToSet = { error: response.error };
          }
          
          if (walletToSet) {
            setWallet(walletToSet);
          }
        }
      } catch (error) {
        setError('Failed to load subscriber data');
      } finally {
        setLoading(false);
        isLoadingDataRef.current = false;
      }
    };
    
    loadData();
  }, [id, currentRole]);
  
  // Function to load medicine booking snapshot (can be called manually)
  const loadMedicineSnapshot = useCallback(async () => {
    if (!bookingId || !currentRole) {
      return;
    }
    
    // Prevent duplicate calls - if already loading, skip
    if (isSnapshotLoadingRef.current) {
      return;
    }
    
      // Mark as loading
      isSnapshotLoadingRef.current = true;
      
      try {
        setMedicineSnapshotLoading(true);
        setMedicineSnapshotError(null);
        
        const params = {
          page: snapshotPage,
          items: SNAPSHOT_PAGE_SIZE
        };
        
        const response = await getMedicineBookingSnapshot(currentRole, bookingId, params);
        
        if (response && response.msg) {
          // Calculate total_pages from pagination if it exists
          if (response.msg.pagination) {
            const calculatedTotalPages = Math.ceil(response.msg.pagination.total / response.msg.pagination.items);
            setMedicineSnapshot({
              ...response.msg,
              pagination: {
                ...response.msg.pagination,
                total_pages: calculatedTotalPages
              }
            });
          } else {
            setMedicineSnapshot(response.msg);
          }
        } else if (response && response.data) {
          // Calculate total_pages from pagination if it exists
          if (response.data.pagination) {
            const calculatedTotalPages = Math.ceil(response.data.pagination.total / response.data.pagination.items);
            setMedicineSnapshot({
              ...response.data,
              pagination: {
                ...response.data.pagination,
                total_pages: calculatedTotalPages
              }
            });
          } else {
            setMedicineSnapshot(response.data);
          }
        } else {
          setMedicineSnapshotError('Failed to load medicine booking snapshot');
        }
      } catch (error) {
        setMedicineSnapshotError('Failed to load medicine booking snapshot');
      } finally {
        setMedicineSnapshotLoading(false);
        isSnapshotLoadingRef.current = false;
      }
  }, [bookingId, currentRole, snapshotPage]);
    
  // Load medicine booking snapshot
  useEffect(() => {
    loadMedicineSnapshot();
  }, [loadMedicineSnapshot]);
  
  // Load booking detail when a row is clicked
  const handleRowClick = async (transactionId, status) => {
    if (!transactionId || !currentRole) return;

    const statusLower = String(status || '').toLowerCase().trim();
    const isInitiatedOrSaved = statusLower === 'saved' || statusLower === 'initiated';

    // Initiated/Saved: only Ops and SuperAdmin can open the Medicine Transaction modal; Finance and Support cannot
    if (isInitiatedOrSaved) {
      if (!isOpsOrSuperAdmin) {
        return; // Do not open any popup for Finance and Support
      }
      setWalletTransactionIdForModal(transactionId); // Store transaction_id to use as wallet_transaction_id
      setMedicineTransactionKey(prev => prev + 1); // Force remount with new key
      setShowMedicineTransactionModal(true);
      return;
    }

    // For other statuses, open the detail modal as before
    setSelectedBookingId(transactionId);
    setDetailLoading(true);
    setDetailError(null);
    setBookingDetail(null);
    
    try {
      const response = await getMedicineBookingDetail(currentRole, transactionId);
      
      if (response && response.msg) {
        setBookingDetail(response.msg);
      } else if (response && response.data) {
        setBookingDetail(response.data);
      } else {
        setDetailError('Failed to load booking details');
      }
    } catch (error) {
      setDetailError('Failed to load booking details');
    } finally {
      setDetailLoading(false);
    }
  };
  
  // Close modal
  const closeModal = () => {
    setSelectedBookingId(null);
    setBookingDetail(null);
    setDetailError(null);
    setPartnerDataExpanded(false);
  };
  
  // open_detail=1: open the detail for the exact transaction named in the URL instead of
  // leaving the list showing. Status comes from the snapshot, never from the URL, so an
  // Initiated/Saved row opens the editable cart exactly as it does inside this module.
  //
  // The subscriber's Transaction History no longer produces these URLs — it renders this
  // module's detail popup in place now — but links already pasted into tickets and Slack
  // still land here, and must still work. The Medicine Subscribers list navigates here
  // without the flag and is unaffected.
  useEffect(() => {
    if (autoOpenedDetailRef.current) return;
    if (searchParams.get('open_detail') !== '1' || !bookingId) return;
    const items = medicineSnapshot?.items;
    if (!Array.isArray(items) || items.length === 0) return;
    // contract: loan-and-subscription internalapi/views/user_type/shared/medicine_subscribers/snapshot.py:421,428,494
    const clicked = items.find((txn) => String(txn.transaction_id) === String(bookingId));
    if (!clicked) return; // not on this snapshot page - leave the list showing
    autoOpenedDetailRef.current = true;
    handleRowClick(clicked.transaction_id, clicked.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicineSnapshot, bookingId, searchParams]);

  // Handle partner data expand - toggle visibility
  const handlePartnerDataExpand = () => {
    setPartnerDataExpanded(!partnerDataExpanded);
  };
  
  // Get next status in the flow based on current status
  const getNextStatus = (currentStatus) => {
    if (!currentStatus) return null;
    const statusLower = String(currentStatus).toLowerCase().trim();
    
    // Status flow: Ordered → Processed → Dispatched → Out for Delivery → Delivered → Complete
    const statusFlow = {
      'ordered': 'processed',
      'processed': 'dispatched',
      'dispatched': 'out_for_delivery',
      'out_for_delivery': 'delivered',
      'out for delivery': 'delivered',
      'delivered': 'complete',
      'complete': null, // Final state
      'cancelled': null, // Final state
      'failed': null, // Final state
      'cancelled/failed': null // Final state
    };
    
    return statusFlow[statusLower] || null;
  };
  
  // Refundable statuses: ordered, processed, dispatched, out_for_delivery, delivered (not refunded)
  const REFUNDABLE_STATUSES = ['ordered', 'processed', 'dispatched', 'out_for_delivery', 'out for delivery', 'delivered'];
  const isRefundableStatus = (status) => {
    const s = String(status || '').toLowerCase().trim();
    return REFUNDABLE_STATUSES.includes(s);
  };

  // Check if transaction is a manual medicine transaction
  const isManualMedicineTransaction = (txn) => {
    // Check if partner_data exists and has items (indicates manual order)
    if (txn.partner_data && Array.isArray(txn.partner_data) && txn.partner_data.length > 0) {
      return true;
    }
    
    // For snapshot list, partner_data might not be included
    // Check if status is one that can be updated (indicates manual order)
    // Statuses that can be updated: ordered, processed, dispatched, out_for_delivery, delivered
    // These are manual order statuses (not third-party bookings)
    const updatableStatuses = ['ordered', 'processed', 'dispatched', 'out_for_delivery', 'out for delivery', 'delivered'];
    const statusLower = String(txn.status || '').toLowerCase().trim();
    
    // If status is in updatable statuses, it's a manual order
    if (updatableStatuses.includes(statusLower)) {
      // Exclude if it has third_party_booking_ref_code (indicates third-party)
      // Manual orders typically don't have third_party_booking_ref_code
      return !txn.third_party_booking_ref_code;
    }
    
    return false;
  };
  
  // Handle status update button click
  const handleStatusUpdateClick = (e, txn) => {
    e.stopPropagation(); // Prevent row click
    const nextStatus = getNextStatus(txn.status);
    if (!nextStatus) return;
    
    setSelectedTransactionForStatus({
      transactionId: txn.transaction_id,
      orderId: txn.order_id || txn.manual_order_id || null, // Store order_id for manual orders
      currentStatus: txn.status,
      nextStatus: nextStatus,
      isManual: isManualMedicineTransaction(txn)
    });
    setThirdPartyOrderId('');
    setShowStatusUpdateModal(true);
  };
  
  // Handle status update submission
  const handleStatusUpdate = async () => {
    if (!selectedTransactionForStatus || !currentRole) return;
    
    const { transactionId, nextStatus } = selectedTransactionForStatus;
    
    // Validate third_party_order_id for processed status
    if (nextStatus === 'processed' && !thirdPartyOrderId.trim()) {
      setDetailError('Third Party Order ID is required for Processed status');
      return;
    }
    
    setUpdatingStatus(true);
    setDetailError(null);
    
    try {
      const response = await updateMedicineBookingStatus(
        currentRole,
        transactionId,
        nextStatus,
        nextStatus === 'processed' ? thirdPartyOrderId.trim() : null
      );
      
      if (response && response.metadata && response.metadata.api_status === true) {
        // Close modal first
        setShowStatusUpdateModal(false);
        setSelectedTransactionForStatus(null);
        setThirdPartyOrderId('');
        
        // Refresh the snapshot data
        if (bookingId) {
          // Reset loading ref to allow refresh
          isSnapshotLoadingRef.current = false;
          
          // Small delay to ensure backend has updated
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Use the loadMedicineSnapshot function to refresh
          await loadMedicineSnapshot();
        }
      } else {
        throw new Error(response?.msg?.error || 'Failed to update status');
      }
    } catch (error) {
      setDetailError(error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Open refund modal
  const handleRefundClick = (e, transactionId) => {
    e.stopPropagation();
    setRefundBookingId(transactionId);
    setRefundError(null);
    setShowRefundModal(true);
  };

  // Submit refund (Finance/SuperAdmin)
  const handleRefundSubmit = async (body) => {
    if (!refundBookingId || !currentRole) return;
    setRefundSubmitting(true);
    setRefundError(null);
    try {
      const response = await refundMedicineOrder(currentRole, refundBookingId, body);
      const message = response?.message || response?.msg?.message || 'Order marked as refunded; cashback reversed.';
      setSuccessToast(message);
      setShowRefundModal(false);
      setRefundBookingId(null);
      isSnapshotLoadingRef.current = false;
      await loadMedicineSnapshot();
    } catch (err) {
      let errorPayload = null;
      try {
        const msg = err?.message || '';
        const jsonMatch = msg.match(/\{[\s\S]*\}$/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed && typeof parsed.error !== 'undefined') errorPayload = parsed.error;
        }
      } catch (_) {
        // ignore parse errors
      }
      setRefundError(errorPayload != null ? errorPayload : (err?.message || 'Refund failed.'));
    } finally {
      setRefundSubmitting(false);
    }
  };

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
          <button 
              onClick={() => navigate('/subscriber-wallet')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
              ← Back to Home
            </button>
            <button 
              onClick={() => navigate(backTarget.path)} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← {backTarget.label}
          </button>
          </div>
        </div>

        {/* Page Heading */}
        <div className="mt-2 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Medicine Booking Profile</h1>
        </div>

        {/* Profile Section */}
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <div className="space-y-6">
            {/* Header Section */}
            <div className="relative">
              <div>
                <div className="text-xl font-bold text-gray-900">{profile?.personal?.full_name || profile?.name || 'Subscriber'}</div>
                <div className="text-sm text-gray-600 flex items-center space-x-2 mt-1">
                  <span>Mobile No: {(isFinance || isSupport) ? (profile?.personal?.mobile || profile?.phoneFull) : (showPhone ? (profile?.personal?.mobile || profile?.phoneFull) : maskedPhone)}</span>
                  {!(isFinance || isSupport) && (
                    <button
                      onClick={() => setShowPhone(v => !v)}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label={showPhone ? 'Hide phone' : 'Show phone'}
                    >
                      {showPhone ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Details Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">REF CODE</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.subscription || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Validity Period</div>
                  <div className="text-sm font-medium text-gray-900">{formatDate(profile?.valid_period)}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Plan</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.plan || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">KYC Status</div>
                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-md bg-green-500 text-white w-fit">{profile?.kyc || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-md w-fit ${
                    profile?.status === 'Active' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                  }`}>
                    {profile?.status || 'N/A'}
                  </span>
                </div>
                <div></div>
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Snapshot */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Wallet Snapshot</h2>
          {wallet?.wallet_snapshot?.ref_code && (
            <div className="text-sm text-gray-500">
              Ref: <span className="font-mono text-gray-700">{wallet.wallet_snapshot.ref_code}</span>
            </div>
          )}
        </div>
        
        {!wallet ? (
          <div className="bg-white border border-gray-200 rounded-md p-6 text-center">
            <div className="text-gray-500 mb-2">Loading wallet data...</div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : wallet.error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
            <div className="text-red-600 mb-2">Failed to load wallet data</div>
            <div className="text-sm text-red-500 mb-3">{wallet.error}</div>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { 
                label: 'WB (Wallet Balance)', 
                value: currencyRounded(wallet?.wallet_snapshot?.balance || 0), 
                icon: <Wallet className="w-4 h-4 text-blue-600" />
              },
              { 
                label: 'EC (Eligible Cashback)', 
                value: currencyRounded(wallet?.wallet_snapshot?.eligible_cashback || 0), 
                icon: <img src={EligibleCashbackIcon} alt="Eligible Cashback" className="w-4 h-4" />
              },
              { 
                label: 'RC (Redeemable Cashback)', 
                value: currencyRounded(wallet?.wallet_snapshot?.redeemable_cashback || 0), 
                icon: <img src={RedeemableCashbackIcon} alt="Redeemable Cashback" className="w-4 h-4" />
              },
              { 
                label: 'LC (Lifetime Cashback)', 
                value: currencyRounded(wallet?.wallet_snapshot?.lifetime_cashback || 0), 
                icon: <img src={LifetimeCashbackIcon} alt="Lifetime Cashback" className="w-4 h-4" />
              },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-md flex items-center justify-center">
                    {c.icon}
                  </div>
                  <div className="text-sm text-gray-600">{c.label}</div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">{c.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Medicine Booking Snapshot */}
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Medicine Booking Snapshot</h2>
          </div>
          
          {medicineSnapshotLoading ? (
            <div className="bg-white border border-gray-200 rounded-md p-6 text-center">
              <div className="text-gray-500 mb-2">Loading medicine booking snapshot...</div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : medicineSnapshotError ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
              <div className="text-red-600 mb-2">Failed to load medicine booking snapshot</div>
              <div className="text-sm text-red-500 mb-3">{medicineSnapshotError}</div>
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : medicineSnapshot?.items ? (
            <>
              {/* Transactions Table */}
              {medicineSnapshot.items && (
                <div className="bg-white border border-gray-200 rounded-md shadow-sm">
                  <div className="p-4 sm:p-6">
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-200 rounded-md min-w-max">
                        <thead className="bg-gray-50">
                          <tr className="border-b border-gray-200">
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date & Time</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Service Provider</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Price</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cashback</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">WB</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Family Members</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Payment Method</th>
                            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Credit Balance</th>
                            {(isOpsOrSuperAdmin || isFinanceOrSuperAdmin) && (
                              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Action</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {medicineSnapshot.items && medicineSnapshot.items.length > 0 ? (
                            medicineSnapshot.items.map((txn) => (
                              <tr 
                                key={txn.transaction_id} 
                                onClick={() => handleRowClick(txn.transaction_id, txn.status)}
                                className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                              >
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">
                                  {(() => {
                                    const dateTimeValue = txn.date_time;
                                    // Ensure we never render an object directly
                                    return formatDateTime(dateTimeValue);
                                  })()}
                                </td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{txn.service_provider || 'N/A'}</td>
                                <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 whitespace-nowrap">
                                  ₹{(txn.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 whitespace-nowrap">
                                  {currencyRounded(txn.cashback || 0)}
                                </td>
                                <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 whitespace-nowrap">
                                  {currencyRounded(txn.wallet_balance || 0)}
                                </td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap" title={txn.family_member_names || ''}>
                                  {txn.family_member_names || 'N/A'}
                                </td>
                                <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                                  <span 
                                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border justify-center"
                                    style={getStatusBadgeStyle(txn.status)}
                                  >
                                    {(() => {
                                      const statusValue = txn.status || 'N/A';
                                      const statusLower = String(statusValue).toLowerCase();
                                      if (statusLower === 'cancelled') {
                                        return 'Cancelled';
                                      }
                                      if (statusLower === 'failed') {
                                        return 'Failed';
                                      }
                                      // Map "out_of_stock" to "Out of Stock" for display
                                      if (statusLower === 'out_of_stock') {
                                        return 'Out of Stock';
                                      }
                                      return statusValue;
                                    })()}
                                  </span>
                                </td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{txn.payment_method || 'N/A'}</td>
                                <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">
                                  {txn.credit_balance != null ? currencyRounded(txn.credit_balance) : '—'}
                                </td>
                                {(isOpsOrSuperAdmin || isFinanceOrSuperAdmin) && (
                                  <td className="px-3 sm:px-6 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                    {(() => {
                                      const statusLower = String(txn.status || '').toLowerCase().trim();
                                      const isRefunded = statusLower === 'refunded';
                                      const showRefund = canRefund && !isRefunded && isRefundableStatus(txn.status);
                                      const isManual = isManualMedicineTransaction(txn);
                                      const nextStatus = getNextStatus(txn.status);
                                      const showNextStatus = isOpsOrSuperAdmin && isManual && nextStatus;

                                      if (isRefunded) {
                                        return <span className="text-xs text-gray-400">-</span>;
                                      }
                                      return (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          {showNextStatus && (
                                            <button
                                              onClick={(e) => handleStatusUpdateClick(e, txn)}
                                              className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                                            >
                                              {nextStatus === 'processed' ? 'Processed' :
                                               nextStatus === 'dispatched' ? 'Dispatched' :
                                               nextStatus === 'out_for_delivery' ? 'Out for Delivery' :
                                               nextStatus === 'delivered' ? 'Delivered' :
                                               nextStatus === 'complete' ? 'Complete' :
                                               nextStatus}
                                            </button>
                                          )}
                                          {showRefund && (
                                            <button
                                              onClick={(e) => handleRefundClick(e, txn.transaction_id)}
                                              className="px-3 py-1.5 text-xs font-medium text-white bg-slate-600 rounded-md hover:bg-slate-700 transition-colors"
                                            >
                                              Refund
                                            </button>
                                          )}
                                          {!showNextStatus && !showRefund && <span className="text-xs text-gray-400">-</span>}
                                        </div>
                                      );
                                    })()}
                                  </td>
                                )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={(isOpsOrSuperAdmin || isFinanceOrSuperAdmin) ? 10 : 9} className="px-6 py-8 text-center text-gray-500">
                                No transactions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {medicineSnapshot.pagination && medicineSnapshot.pagination.total > 0 && (
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          Showing {((medicineSnapshot.pagination.page - 1) * medicineSnapshot.pagination.items) + 1}-{Math.min(medicineSnapshot.pagination.page * medicineSnapshot.pagination.items, medicineSnapshot.pagination.total)} of {medicineSnapshot.pagination.total}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            disabled={medicineSnapshot.pagination.page === 1}
                            onClick={() => setSnapshotPage((p) => Math.max(1, p - 1))}
                            className={`px-3 py-1 border rounded-md text-sm ${medicineSnapshot.pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                          >
                            Prev
                          </button>
                          {Array.from({ length: medicineSnapshot.pagination.total_pages || 1 }).map((_, i) => {
                            const pageNum = i + 1;
                            const totalPages = medicineSnapshot.pagination.total_pages || 1;
                            const shouldShow = 
                              pageNum === 1 || 
                              pageNum === totalPages || 
                              (pageNum >= medicineSnapshot.pagination.page - 2 && pageNum <= medicineSnapshot.pagination.page + 2);
                            
                            if (!shouldShow) {
                              if (pageNum === medicineSnapshot.pagination.page - 3 || pageNum === medicineSnapshot.pagination.page + 3) {
                                return (
                                  <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                                    ...
                                  </span>
                                );
                              }
                              return null;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setSnapshotPage(pageNum)}
                                className={`px-3 py-1 border rounded-md text-sm ${medicineSnapshot.pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            disabled={medicineSnapshot.pagination.page >= (medicineSnapshot.pagination.total_pages || 1)}
                            onClick={() => setSnapshotPage((p) => Math.min(medicineSnapshot.pagination.total_pages || 1, p + 1))}
                            className={`px-3 py-1 border rounded-md text-sm ${medicineSnapshot.pagination.page >= (medicineSnapshot.pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
        
        {/* Booking Detail Modal — shared with the subscriber portal, which renders the
            same component in place instead of navigating here. */}
        <MedicineBookingDetailModal
          mounted={mounted && !!selectedBookingId}
          bookingDetail={bookingDetail}
          loading={detailLoading}
          error={detailError}
          onRetry={() => handleRowClick(selectedBookingId)}
          onClose={closeModal}
          showFullMobile={!isSupport && mobilesUnmasked}
          partnerDataExpanded={partnerDataExpanded}
          onTogglePartnerData={handlePartnerDataExpand}
          getStatusStyle={getStatusBadgeStyle}
        />
        
        {/* Medicine Transaction Modal */}
        {mounted && showMedicineTransactionModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">Manual Transaction - Medicine</h2>
                <button
                  onClick={() => setShowMedicineTransactionModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <MedicineTransaction 
                  key={`medicine-transaction-${id}-${medicineTransactionKey}`}
                  applicationId={id}
                  walletTransactionId={walletTransactionIdForModal}
                  isModal={true}
                  onClose={() => {
                    setShowMedicineTransactionModal(false);
                    setWalletTransactionIdForModal(null);
                  }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Refund Modal (Finance and SuperAdmin) */}
        {mounted && (
          <MedicineRefundModal
            mounted={mounted}
            show={showRefundModal}
            onClose={() => {
              if (!refundSubmitting) {
                setShowRefundModal(false);
                setRefundBookingId(null);
                setRefundError(null);
              }
            }}
            onSubmit={handleRefundSubmit}
            submitting={refundSubmitting}
            error={refundError}
          />
        )}

        {/* Success toast for refund */}
        {successToast && (
          <div className="fixed bottom-4 right-4 z-[10000] px-4 py-3 bg-green-600 text-white rounded-lg shadow-lg text-sm animate-fade-in">
            {successToast}
            <button
              type="button"
              onClick={() => setSuccessToast(null)}
              className="ml-3 text-white/90 hover:text-white"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Status Update Modal */}
        {mounted && showStatusUpdateModal && selectedTransactionForStatus && createPortal(
          <div 
            className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50" 
            onClick={() => {
              if (!updatingStatus) {
                setShowStatusUpdateModal(false);
                setSelectedTransactionForStatus(null);
                setThirdPartyOrderId('');
                setDetailError(null);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Update Order Status</h3>
                <button
                  onClick={() => {
                    if (!updatingStatus) {
                      setShowStatusUpdateModal(false);
                      setSelectedTransactionForStatus(null);
                      setThirdPartyOrderId('');
                      setDetailError(null);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={updatingStatus}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="px-6 py-4 space-y-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Current Status</div>
                  <div className="text-base font-medium text-gray-900">{selectedTransactionForStatus.currentStatus || 'N/A'}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600 mb-1">Update To</div>
                  <div className="text-base font-medium text-blue-600">
                    {selectedTransactionForStatus.nextStatus === 'processed' ? 'Processed' :
                     selectedTransactionForStatus.nextStatus === 'dispatched' ? 'Dispatched' :
                     selectedTransactionForStatus.nextStatus === 'out_for_delivery' ? 'Out for Delivery' :
                     selectedTransactionForStatus.nextStatus === 'delivered' ? 'Delivered' :
                     selectedTransactionForStatus.nextStatus === 'complete' ? 'Complete' :
                     selectedTransactionForStatus.nextStatus}
                  </div>
                </div>
                
                {selectedTransactionForStatus.nextStatus === 'processed' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Third Party Order ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={thirdPartyOrderId}
                      onChange={(e) => setThirdPartyOrderId(e.target.value)}
                      placeholder="Enter provider order ID"
                      disabled={updatingStatus}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-gray-500">Required when status is "Processed"</p>
                  </div>
                )}
                
                {detailError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                    {detailError}
                  </div>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    if (!updatingStatus) {
                      setShowStatusUpdateModal(false);
                      setSelectedTransactionForStatus(null);
                      setThirdPartyOrderId('');
                      setDetailError(null);
                    }
                  }}
                  disabled={updatingStatus}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={updatingStatus || (selectedTransactionForStatus.nextStatus === 'processed' && !thirdPartyOrderId.trim())}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    updatingStatus || (selectedTransactionForStatus.nextStatus === 'processed' && !thirdPartyOrderId.trim())
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {updatingStatus ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default MedicineBookingProfile;

